import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Select,
  MenuItem,
} from "@material-ui/core";
import NoDataIcon from "../images/no-data-icon.svg";
import PropTypes from "prop-types";
import moment from "moment";
import ArrowDownIcon from "./ArrowDownIcon";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #DEE2E6",
    borderRadius: "8px",
    overflow: "hidden",
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: "#E4DAFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "20px",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #DEE2E6",
  },
  sortIcon: {
    width: 12,
    height: 12,
    marginLeft: theme.spacing(0.5),
    cursor: "pointer",
    color: "#495057",
  },
  headerCellContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    display: "flex",
    alignItems: "center",
  },
  bodyCell: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: "#212529",
    lineHeight: "100%",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #D5E0EB",
  },
  checkboxCell: {
    padding: theme.spacing(1, 1.5),
  },
  expandIconCell: {
    padding: theme.spacing(1, 1.5),
    width: "40px",
  },
  expandIcon: {
    width: 16,
    height: 16,
    color: "#495057",
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
  },
  expandedRow: {
    backgroundColor: "#FFFFFF",
  },
  expandedContent: {
    padding: 0,
    borderTop: "1px solid #D5E0EB",
  },
  nestedTableContainer: {
    maxHeight: "400px",
    overflowY: "auto",
    backgroundColor: "#FFFFFF",
  },
  nestedTableTitle: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 600,
    color: "#212529",
    padding: theme.spacing(2, 2, 1, 2),
    margin: 0,
  },
  nestedTable: {
    width: "100%",
  },
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 0",
    color: "#ADB5BD",
  },
  emptyStateIcon: {
    width: 24,
    height: 24,
    marginBottom: 6,
    color: "#ADB5BD",
  },
  emptyStateText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "100%",
    textAlign: "center",
  },
  nestedHeaderCell: {
    backgroundColor: "#E4DAFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "20px",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #DEE2E6",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  nestedBodyCell: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#212529",
    lineHeight: "20px",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #D5E0EB",
  },
  nestedRow: {
    backgroundColor: "#FFFFFF",
    "&:nth-of-type(even)": {
      backgroundColor: "#F8F9FA",
    },
  },
  row: {
    backgroundColor: "#FFFFFF",
    borderBottom: "1px solid #D5E0EB",
    "&:nth-of-type(odd)": {
      backgroundColor: "#FFFFFF",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#F8F9FA",
    },
    "&:hover": {
      backgroundColor: "#F1F3F5",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid #DEE2E6",
    backgroundColor: "#FFFFFF",
  },
  paginationInfo: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    color: "#495057",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  paginationSelect: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: "60px",
    "& .MuiSelect-root": {
      padding: theme.spacing(0.5, 2, 0.5, 1),
      fontSize: "14px",
      height: "32px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiOutlinedInput-root": {
      height: "32px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#CED4DA",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ADB5BD",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6F42C1",
      borderWidth: "1px",
    },
    "& .MuiSelect-icon": {
      color: "#495057",
      right: theme.spacing(1),
      top: "50%",
      transform: "translateY(-50%)",
    },
  },
  paginationButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  paginationButton: {
    minWidth: "32px",
    height: "32px",
    padding: 0,
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#6C757D",
    backgroundColor: "#FFFFFF",
    border: "1px solid #CED4DA",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    "&:hover:not(:disabled)": {
      backgroundColor: "#F8F9FA",
    },
    "&:disabled": {
      backgroundColor: "#E1E1E1",
      color: "#8F8F8F",
      borderColor: "#E1E1E1",
      cursor: "not-allowed",
    },
  },
  paginationButtonActive: {
    color: "#6F42C1",
    borderColor: "#6F42C1",
    backgroundColor: "#FFFFFF",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#FFFFFF",
    },
  },
  paginationArrow: {
    width: "16px",
    height: "16px",
  },
}));

/**
 * DataTable Component
 * 
 * A reusable data table component with sorting, checkboxes, and pagination
 * 
 * @param {Array} columns - Array of column definitions with { key, label, sortable, render }
 * @param {Array} data - Array of data objects
 * @param {boolean} showCheckbox - Whether to show checkboxes
 * @param {boolean} showExpandIcon - Whether to show expand icon in first column
 * @param {Function} renderExpandedContent - Function to render expanded content (row, index) => ReactNode
 * @param {Function} onRowSelect - Handler for row selection (selectedIds) => void
 * @param {boolean} pagination - Whether to show pagination
 * @param {number} totalItems - Total number of items for pagination
 * @param {Function} onPageChange - Handler for page change (page, rowsPerPage) => void
 * @param {string} className - Additional CSS classes
 */
const DataTable = ({
  columns,
  data,
  showCheckbox = false,
  showExpandIcon = false,
  renderExpandedContent,
  onRowSelect,
  isRowSelected, // Function to check if a row is selected: (row) => boolean
  pagination = false,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSize: externalPageSize,
  page: externalPage,
  emptyState,
  className,
  clearSelectionSignal,
  isLoading,
}) => {
  const classes = useStyles();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedRows, setSelectedRows] = useState(new Set());
  const onRowSelectRef = useRef(onRowSelect);

  useEffect(() => {
    onRowSelectRef.current = onRowSelect;
  }, [onRowSelect]);

  useEffect(() => {
    if (clearSelectionSignal === undefined) return;
    setSelectedRows(new Set());
    if (onRowSelectRef.current) {
      onRowSelectRef.current([], []);
    }
  }, [clearSelectionSignal]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(externalPageSize || 10);

  // Sync external pageSize with internal state
  useEffect(() => {
    if (externalPageSize !== undefined) {
      setRowsPerPage(externalPageSize);
      // Reset to first page when page size changes externally
      setPage(0);
    }
  }, [externalPageSize]);

  // Sync external page with internal state (convert from 1-based to 0-based)
  useEffect(() => {
    if (externalPage !== undefined) {
      setPage(externalPage - 1);
    }
  }, [externalPage]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // For server-side pagination, only select current page items
      const dataSource = pagination && totalItems > 0 && totalItems !== data.length
        ? paginatedData
        : data;
      const startIndex = pagination && totalItems > 0 && totalItems !== data.length
        ? page * rowsPerPage
        : 0;
      const newSelected = new Set(dataSource.map((_, index) => startIndex + index));
      setSelectedRows(newSelected);
      if (onRowSelect) {
        onRowSelect(Array.from(newSelected), dataSource);
      }
    } else {
      // For server-side pagination, only deselect current page items
      if (pagination && totalItems > 0 && totalItems !== data.length) {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + paginatedData.length;
        const newSelected = new Set(selectedRows);
        for (let i = startIndex; i < endIndex; i++) {
          newSelected.delete(i);
        }
        setSelectedRows(newSelected);
        if (onRowSelect) {
          const remainingSelected = Array.from(newSelected);
          // Get data for remaining selections (only current page)
          const remainingData = paginatedData.filter((_, i) => {
            const absoluteIndex = startIndex + i;
            return newSelected.has(absoluteIndex);
          });
          onRowSelect(remainingSelected, remainingData);
        }
      } else {
        setSelectedRows(new Set());
        if (onRowSelect) {
          onRowSelect([], []);
        }
      }
    }
  };

  const handleSelectRow = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
    if (onRowSelect) {
      // Get the actual row data using the index
      // For server-side pagination, index is absolute, so we need to convert to page-relative
      let selectedData;
      if (pagination && totalItems > 0 && totalItems !== data.length) {
        // Server-side pagination: filter paginatedData using page-relative indices
        const pageRelativeIndices = Array.from(newSelected)
          .map(absoluteIndex => absoluteIndex - (page * rowsPerPage))
          .filter(relIndex => relIndex >= 0 && relIndex < paginatedData.length);
        selectedData = paginatedData.filter((_, i) => pageRelativeIndices.includes(i));
      } else {
        // Client-side pagination: use absolute indices with sortedData
        selectedData = sortedData.filter((_, i) => newSelected.has(i));
      }
      onRowSelect(Array.from(newSelected), selectedData);
    }
  };

  const handleExpandRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle date sorting
    if (moment.isMoment(moment(aValue)) || (typeof aValue === "string" && moment(aValue).isValid())) {
      aValue = moment(aValue).unix();
      bValue = moment(bValue).unix();
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  // For server-side pagination, don't slice the data
  // The data is already paginated from the API
  // If totalItems is provided and different from data.length, assume server-side pagination
  const paginatedData = pagination && totalItems > 0 && totalItems !== data.length
    ? sortedData // Server-side pagination: data is already paginated
    : pagination
    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) // Client-side pagination
    : sortedData;

  const SortIcon = () => (
    <svg
      className={classes.sortIcon}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
    </svg>
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (onPageChange) {
      // Convert from 0-based to 1-based for API
      onPageChange(newPage + 1, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (onPageChange) {
      // Convert from 0-based to 1-based for API
      onPageChange(1, newRowsPerPage);
    }
    if (onPageSizeChange) {
      onPageSizeChange(newRowsPerPage);
    }
  };

  // Calculate select all state based on isRowSelected if provided, otherwise use selectedRows
  const allSelected = isRowSelected
    ? paginatedData.length > 0 && paginatedData.every(row => isRowSelected(row))
    : data.length > 0 && selectedRows.size === data.length;
  const someSelected = isRowSelected
    ? paginatedData.some(row => isRowSelected(row)) && !allSelected
    : selectedRows.size > 0 && selectedRows.size < data.length;

  return (
    <div>
      <TableContainer component={Paper} className={`${classes.tableContainer} ${className || ""}`}>
        <Table className={classes.table} aria-label="data table">
          <TableHead>
            <TableRow>
              {showExpandIcon && (
                <TableCell className={classes.headerCell} style={{ width: "40px" }}>
                </TableCell>
              )}
              {showCheckbox && (
                <TableCell className={classes.headerCell} padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={classes.headerCell}
                  onClick={() => column.sortable && handleSort(column.key)}
                  style={{ cursor: column.sortable ? "pointer" : "default" }}
                >
                  <div className={classes.headerCellContent}>
                    <div className={classes.headerText}>
                      {column.label}
                      {column.sortable && <SortIcon />}
                    </div>
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 10 }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className={classes.row}>
                  {showExpandIcon && (
                    <TableCell className={classes.expandIconCell}>
                      <div
                        style={{
                          height: 10,
                          backgroundColor: "#F1F3F5",
                          borderRadius: 4,
                          width: "100%",
                        }}
                      />
                    </TableCell>
                  )}
                  {showCheckbox && (
                    <TableCell className={classes.checkboxCell} padding="checkbox">
                      <div
                        style={{
                          height: 10,
                          backgroundColor: "#F1F3F5",
                          borderRadius: 4,
                          width: "100%",
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell key={column.key} className={classes.bodyCell}>
                      <div
                        style={{
                          height: 10,
                          backgroundColor: "#F1F3F5",
                          borderRadius: 4,
                          width: "100%",
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={
                    (showExpandIcon ? 1 : 0) +
                    (showCheckbox ? 1 : 0) +
                    columns.length
                  }
                  style={{ padding: 0, border: "none" }}
                >
                  {emptyState || (
                    <div className={classes.emptyStateContainer}>
                      <img
                        src={NoDataIcon}
                        alt="No data"
                        className={classes.emptyStateIcon}
                      />
                      <span className={classes.emptyStateText}>
                        No data available
                      </span>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const actualIndex = pagination ? page * rowsPerPage + index : index;
                // Use isRowSelected function if provided, otherwise fall back to index-based selection
                const isSelected = isRowSelected 
                  ? isRowSelected(row)
                  : selectedRows.has(actualIndex);
                const isExpanded = expandedRows.has(actualIndex);
                return (
                  <React.Fragment key={actualIndex}>
                    <TableRow
                      className={classes.row}
                      style={{
                        backgroundColor: actualIndex % 2 === 0 ? "#FFFFFF" : "#F8F9FA",
                      }}
                    >
                      {showExpandIcon && (
                        <TableCell className={classes.expandIconCell}>
                          <svg
                            className={classes.expandIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            onClick={() => handleExpandRow(actualIndex)}
                            style={{
                              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          >
                            <path
                              d="M7 10L12 15L17 10H7Z"
                              fill="currentColor"
                            />
                          </svg>
                        </TableCell>
                      )}
                      {showCheckbox && (
                        <TableCell className={classes.checkboxCell} padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectRow(actualIndex)}
                            color="primary"
                          />
                        </TableCell>
                      )}
                      {columns.map((column) => (
                        <TableCell key={column.key} className={classes.bodyCell}>
                          {column.render
                            ? column.render(row[column.key], row, actualIndex)
                            : row[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                    {isExpanded && renderExpandedContent && (
                      <TableRow>
                        <TableCell
                          colSpan={
                            (showExpandIcon ? 1 : 0) +
                            (showCheckbox ? 1 : 0) +
                            columns.length
                          }
                          className={classes.expandedContent}
                          style={{ padding: 0 }}
                        >
                          {renderExpandedContent(row, actualIndex)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (() => {
        // Use totalItems if provided (server-side), otherwise use data.length (client-side)
        const total = totalItems > 0 ? totalItems : data.length;
        const totalPages = Math.ceil(total / rowsPerPage);
        const maxVisiblePages = 5;
        let startPage = Math.max(0, page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage < maxVisiblePages - 1) {
          startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
          pageNumbers.push(i);
        }

        return (
          <div className={classes.paginationContainer}>
            <div className={classes.paginationInfo}>
              <span>Showing</span>
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                className={classes.paginationSelect}
                variant="outlined"
                IconComponent={ArrowDownIcon}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
              <span>of {total} items</span>
            </div>
            <div className={classes.paginationButtons}>
              <button
                className={classes.paginationButton}
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 0}
                aria-label="Previous page"
              >
                <svg
                  className={classes.paginationArrow}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {pageNumbers.map((pageNum) => (
                <button
                  key={pageNum}
                  className={`${classes.paginationButton} ${
                    page === pageNum ? classes.paginationButtonActive : ""
                  }`}
                  onClick={() => handleChangePage(null, pageNum)}
                >
                  {pageNum + 1}
                </button>
              ))}
              <button
                className={classes.paginationButton}
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= totalPages - 1}
                aria-label="Next page"
              >
                <svg
                  className={classes.paginationArrow}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  showCheckbox: PropTypes.bool,
  showExpandIcon: PropTypes.bool,
  renderExpandedContent: PropTypes.func,
  onRowSelect: PropTypes.func,
  isRowSelected: PropTypes.func, // Function to check if a row is selected: (row) => boolean
  pagination: PropTypes.bool,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  emptyState: PropTypes.node,
  className: PropTypes.string,
  clearSelectionSignal: PropTypes.number,
  isLoading: PropTypes.bool,
};

DataTable.defaultProps = {
  showCheckbox: false,
  showExpandIcon: false,
  renderExpandedContent: null,
  onRowSelect: null,
  isRowSelected: null,
  pagination: false,
  totalItems: 0,
  onPageChange: null,
  emptyState: null,
  className: "",
  clearSelectionSignal: undefined,
  isLoading: false,
};

export default DataTable;

